import React, { useEffect } from "react";
import AccordionToggle from "components/forms/AccordionToggle/AccordionToggle";
import Accordion from 'react-bootstrap/Accordion';
import Card from "react-bootstrap/Card";
import { NumberField } from "components/forms";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { setWASMApplicationState } from 'store/wasmApplication/wasmApplication.slice';
import Table from 'react-bootstrap/Table';
import { GetTreeNames } from 'services/RunApplication';
import SpeciesInputRow from './SpeciesInputRow';

export interface IComponentState {
  gammaRootReinforcement: number,
  characteristicValueCoefficient: number
}

export default function SoilThickness() {
  const dispatch = useDispatch()
  const WASMApplicationState = useSelector((state: RootState) => state.WASMApplication);

  const initial_state: IComponentState = {
    gammaRootReinforcement: WASMApplicationState.gammaRootReinforcement,
    characteristicValueCoefficient: WASMApplicationState.characteristicValueCoefficient
  }

  const [parameters, setParameters] = React.useState<IComponentState>(initial_state);
  const [selected_species, setSelectedSpecies] = React.useState<number[]>(Object.keys(WASMApplicationState.speciesDensity).map(Number) );
  const [species, setSpecies] = React.useState<{name: string, index: number}[]>([]);
  const [speciesName, setSpeciesName] = React.useState<string[]>([]);

  /**  
     * Update the local state while the component is changing.
     * @param event HTML Input Event
     */
  function handleTextBoxOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    setParameters({
      ...parameters,
      [event.target.name]:
        event.target.value === ""
          ? event.target.value
          : parseFloat(event.target.value)
    });
  }

  /**
   * Update global state once the control is unfocused
   * @param event onBlur Event
   */
  function handleNumberFieldOnBlur(event: React.FocusEvent<HTMLInputElement>) {
    dispatch(setWASMApplicationState({
      ...WASMApplicationState,
      ...parameters
    }));
  }

  /**
   * Upon first load, get the tree names.
   */
  useEffect(() => {
    GetTreeNames().then((msg) => {
      const results = msg.data;
      const species = [];
      const species_complete: string[] = [];
      for (let i = 0; i < results.size(); i++) {
        const specie: string = results.get(i);
  
        species_complete.push(specie);

        //If item is already in selected, let's not add it here
        console.log("Loading Trees Again")
        if (selected_species.includes( i )) {
          continue;
        }

        species.push({ name: specie, index: i});
      }
      species.sort( (a, b) => a.name.localeCompare(b.name));
      setSpecies(species);

      setSpeciesName(species_complete)

      results.delete();
    
    });
  }, []);

  function handleTreesOnSelect(eventKey: any, event: React.SyntheticEvent<unknown, Event>): any {
    //@ts-ignore
    const tree_name = event.target.text;
    const v = [...selected_species];
    v.push(eventKey);
    setSelectedSpecies(v);

    // Remove tree from options
    const v1 = species.filter(s => s.name !== tree_name);
    v1.sort();
    setSpecies(v1);

    // Update values for initialization
    const index = eventKey;
    if (index === undefined) {
      console.error(`We have an undefined species: ${species}`);
      throw new Error(`We have an undefined species: ${species}`);
    }
    const speciesDensity = { ...WASMApplicationState.speciesDensity};
    speciesDensity[index] = 100;

    const treeDBH = {...WASMApplicationState.treeDBH};
    treeDBH[index] = 10;

    dispatch(setWASMApplicationState({
      ...WASMApplicationState,
      speciesDensity: speciesDensity,
      treeDBH: treeDBH,
      species: speciesName
    }));
  }

  function getTableBody() {
    if (selected_species.length === 0) {
      return <tbody>
        <tr><td>Select a species...</td></tr>
      </tbody>
    }
//    selected_species.sort( (a, b) => a.name.localeCompare(b.name));
    selected_species.sort();
    return <tbody>
      {
        selected_species.map((index) => {

          console.log(index);
          var name = speciesName.at(index);
            
          if (name === undefined ) {
            name = "Loading..."
          }

          return (
            <SpeciesInputRow key={"input_box_species" + index} speciesName={name} speciesIndex={index} />
          )
        })
      }
    </tbody>
  }

  const form_name = "Forest Characteristics";

  return (
    <Card>
      <Card.Header>
        <AccordionToggle eventKey={form_name}><span className="forest-char-header">➥</span>{form_name}{" "}</AccordionToggle>
      </Card.Header>
      <Accordion.Collapse eventKey={form_name}>
        <Container>
          <Row>
            <Col md={6} sm={12}>
              <NumberField
                label="Characteristic Value Coefficient"
                unit=""
                step="0.01"
                min={1}
                name="characteristicValueCoefficient"
                value={parameters.characteristicValueCoefficient}
                onChange={handleTextBoxOnChange}
                onBlur={handleNumberFieldOnBlur}
              />
            </Col>
            <Col md={6} sm={12}>

              <NumberField
                label="Partial Safety Factor for Root Reinforcement"
                unit=""
                step="0.01"
                min={1}
                name="gammaRootReinforcement"
                value={parameters.gammaRootReinforcement}
                onChange={handleTextBoxOnChange}
                onBlur={handleNumberFieldOnBlur}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DropdownButton onSelect={handleTreesOnSelect} title="Add Tree Species" disabled={species.length === 0} id="dropdown-basic">
                {
                  species.map((specie, index) => {
                    return <Dropdown.Item eventKey={specie.index} key={`dropdown-item-${specie.name}`}>{specie.name}</Dropdown.Item>
                  })
                }
              </DropdownButton>
            </Col>
          </Row>
          <Table bordered>
            <thead>
              <tr>
                <th>Species</th>
                <th>Density Horizontal Projection [trees/ha]</th>
                <th>DBH [cm]</th>
              </tr>
            </thead>
            {getTableBody()}
          </Table>
        </Container>
      </Accordion.Collapse>
    </Card>
  );
}


/*
<Row>
          <Col md={6} sm={12}>
            <NumberField
              label="Saturated Thickness Fraction"
              unit=""
              name="saturatedThicknessFraction"
              value={parameters.saturatedThicknessFraction}
              onChange={handleTextBoxOnChange}
            />
          </Col>
          <Col md={6} sm={12}>
            <NumberField
              label="Rainfall Rate"
              unit="mm/hr"
              name="rainfallRate"
              value={parameters.rainfallRate}
              onChange={handleTextBoxOnChange}
            />
          </Col>
        </Row>*/
